import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BelongingCover from './BelongingCover';
import './BelongingComingSoon.css';

const LAST_UPDATED = process.env.REACT_APP_BUILD_DATE
  || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const WAITLIST_ENDPOINT = process.env.REACT_APP_BELONGING_WAITLIST_URL;

export default function BelongingComingSoon() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email.');
      return;
    }

    setStatus('loading');

    try {
      if (WAITLIST_ENDPOINT) {
        const res = await fetch(WAITLIST_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email: trimmed, project: 'Belonging' }),
        });
        if (!res.ok) throw new Error('Request failed');
      } else {
        const subject = encodeURIComponent('Belonging waitlist');
        const body = encodeURIComponent(`Please notify me when Belonging launches:\n\n${trimmed}`);
        window.location.href = `mailto:mixinyi@cca.edu?subject=${subject}&body=${body}`;
      }
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setError('Something went wrong. Try again or email mixinyi@cca.edu directly.');
    }
  };

  return (
    <div className="belong-page">
      <div className="belong-layout">
        <aside className="belong-sidebar" aria-label="Page navigation">
          <button type="button" className="belong-back" onClick={() => navigate('/')}>
            ← Return back home
          </button>
          <p className="belong-copyright">
            ©2026 Yaya Hagen
            <br />
            <span className="belong-copyright-muted">Last updated {LAST_UPDATED}</span>
          </p>
        </aside>

        <div className="belong-content">
          <p className="belong-breadcrumb">
            <button type="button" className="belong-breadcrumb-link" onClick={() => navigate('/')}>
              Home
            </button>
            <span className="belong-breadcrumb-muted"> / Belonging</span>
          </p>

          <header className="belong-hero">
            <div className="belong-title-row">
              <h1 className="belong-title">Belonging</h1>
              <div className="belong-tag-row">
                <span className="belong-tag">Product Design</span>
                <span className="belong-tag">Storytelling</span>
                <span className="belong-tag">Coming soon</span>
              </div>
            </div>
            <p className="belong-subtitle">
              An interactive storytelling app for kids exploring cultural identity
            </p>
          </header>

          <section className="belong-header-grid" aria-label="Project summary">
            <div className="belong-header-col">
              <h2 className="belong-label">Details</h2>
              <dl className="belong-meta-list">
                <dt>Role</dt>
                <dd>Product Designer</dd>
                <dt>Timeline</dt>
                <dd>2026 · In progress</dd>
                <dt>Skills</dt>
                <dd className="belong-meta-skills">Product Design, UX Research, Storytelling</dd>
                <dt>Team</dt>
                <dd>Independent project</dd>
                <dt>Status</dt>
                <dd>Coming soon</dd>
              </dl>
            </div>

            <div className="belong-header-col">
              <h2 className="belong-label">Overview</h2>
              <p className="belong-p">
                Belonging is a gamified mini-game collection app that teaches kids ages
                9–14 real skills for navigating cultural identity through interactive
                scenarios and storytelling.
              </p>
              <p className="belong-p">
                Kids from immigrant families, mixed heritage, or any background different
                from their peers often feel isolated and invisible. Belonging gives them
                a safe space to explore who they are—through play, meaningful choices,
                and stories where they finally see themselves represented.
              </p>
            </div>
          </section>

          <section className="belong-media" aria-label="Project visuals">
            <div className="belong-cover-wrap">
              <BelongingCover variant="page" />
            </div>

            <div className="belong-background">
              <h2 className="belong-label">Background</h2>
              <div className="belong-copy belong-copy--full">
                  <p>
                    Growing up in China as a &ldquo;wasian,&rdquo; I felt caught between two worlds.
                    I was so little I didn&apos;t understand why I wasn&apos;t being welcomed like
                    other kids. But I know I&apos;m not the only kid who feels this way.
                  </p>
                  <p>
                    Kids don&apos;t see themselves in the stories they read. They don&apos;t have
                    the language to describe feeling caught between cultures—and without
                    early intervention, that isolation can follow them into adulthood.
                  </p>
                  <p>
                    I&apos;m building Belonging so kids can learn essential identity skills:
                    talking to parents about culture, handling microaggressions, finding
                    &ldquo;their people,&rdquo; and celebrating heritage. Through their stories,
                    they discover they&apos;re <span className="belong-accent">not alone</span>
                    —and find <span className="belong-accent">pride in who they are</span>.
                  </p>
              </div>
            </div>
          </section>

          <section className="belong-section" id="problem" aria-labelledby="belong-problem-label">
            <h2 id="belong-problem-label" className="belong-label">Problem</h2>
            <p className="belong-section-lead">
              Kids ages 9–14 need age-appropriate ways to explore cultural identity—not
              more silence when they feel caught between worlds.
            </p>

            <ol className="belong-list">
              <li>Kids feel invisible and don&apos;t see themselves represented.</li>
              <li>No age-appropriate tools to navigate cultural identity.</li>
              <li>Lack of guidance on handling microaggressions and belonging.</li>
              <li>Limited access to mentorship and community.</li>
            </ol>

            <p className="belong-p">
              Belonging is a gamified mini-game collection app that teaches kids real
              skills for navigating cultural identity through interactive scenarios
              and storytelling.
            </p>
            <p className="belong-p">
              Through Belonging, kids learn essential identity skills like talking to
              parents about culture, handling microaggressions, finding &ldquo;their
              people,&rdquo; and celebrating heritage.
            </p>

            <p className="belong-subhead">By allowing the ability to:</p>
            <ol className="belong-list">
              <li>
                Play scenario-based mini-games (e.g., Family Conversation, Classroom
                Moments, Finding Your People, Cultural Pride)
              </li>
              <li>Make meaningful choices that teach emotional intelligence</li>
              <li>Unlock stories and earn badges as they progress</li>
            </ol>

            <p className="belong-lead">
              We will help kids 9–14 feel less alone and more confident in their identity.
            </p>
          </section>

          <section className="belong-section" id="research" aria-labelledby="belong-research-label">
            <h2 id="belong-research-label" className="belong-label">Research</h2>
            <p className="belong-p belong-p--meta">03 · Research &amp; Insights</p>
            <p className="belong-section-lead">
              Identity struggles in childhood don&apos;t stay in childhood—they shape
              confidence, anxiety, and self-esteem well into adulthood.
            </p>

            <p className="belong-p">
              Many young people struggle with identity and belonging, especially those
              from immigrant or mixed-heritage backgrounds. Without early intervention
              and representation, these feelings of isolation can lead to anxiety and
              low self-esteem in adulthood.
            </p>

            <p className="belong-subhead">Key Research Findings</p>
            <p className="belong-note">From my own experience:</p>
            <ul className="belong-list">
              <li>
                Kids don&apos;t have the language to describe feeling caught between cultures
              </li>
              <li>They need to see themselves represented in media/stories</li>
              <li>
                Mentorship from older kids who&apos;ve navigated this helps immensely
              </li>
              <li>
                Small wins (handling a difficult situation well) build confidence
              </li>
            </ul>

            <p className="belong-subhead">Game Design Approach</p>
            <p className="belong-p">
              Research shows that scenario-based learning games are highly effective
              for teaching emotional intelligence and social skills to kids ages
              8–14. By making these scenarios interactive and rewarding, kids stay
              engaged while learning real coping strategies.
            </p>
          </section>

          <section className="belong-signup" aria-labelledby="belong-signup-heading">
            <h2 id="belong-signup-heading" className="belong-signup-label">
              Get notified when it launches
            </h2>
            <p className="belong-signup-note">
              Leave your email and I&apos;ll let you know when Belonging is ready.
            </p>

            {status === 'success' ? (
              <p className="belong-signup-success" role="status">
                Thank you — you&apos;re on the list. I&apos;ll be in touch soon.
              </p>
            ) : (
              <form className="belong-form" onSubmit={handleSubmit} noValidate>
                <label className="belong-form-field">
                  <span className="visually-hidden">Email address</span>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    placeholder="you@email.com"
                    autoComplete="email"
                    disabled={status === 'loading'}
                    required
                  />
                </label>
                <button type="submit" className="belong-form-btn" disabled={status === 'loading'}>
                  {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
                </button>
              </form>
            )}

            {error && (
              <p className="belong-form-error" role="alert">{error}</p>
            )}
          </section>

          <p className="belong-closing">Coming soon.</p>
        </div>
      </div>
    </div>
  );
}
